/**
 * Current-user id, read locally instead of over the network.
 *
 * `supabase.auth.getUser()` looks like a local accessor but is not: auth-js
 * always issues `GET /auth/v1/user` for it, and it takes the client's auth
 * mutex first (`GoTrueClient._acquireLock(-1, ...)` around `_getUser`). Two
 * services calling it before their queries therefore paid two *serialized*
 * network round-trips — even inside a single `Promise.all` — before either
 * query was allowed to start.
 *
 * The session already lives in local storage and `onAuthStateChange` reports
 * every change to it, so the id can be served from memory after the first
 * resolution. This is only ever used as a query filter and a cache key —
 * ownership is enforced by RLS on the server, not by this value.
 */

import { supabase } from './supabase';

// `undefined` = not resolved yet, `null` = resolved and signed out.
let cachedUserId: string | null | undefined;
let inflight: Promise<string | null> | null = null;

// Fires with INITIAL_SESSION as soon as the client finishes initializing, and
// again on every sign-in, sign-out and token refresh.
supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
});

/**
 * The id if it is already known, otherwise `undefined`. Synchronous — for
 * callers that want to skip work entirely rather than await.
 */
export function peekCurrentUserId(): string | null | undefined {
  return cachedUserId;
}

/**
 * The id, resolving from the stored session on first call.
 *
 * `getSession()` reads local storage; it only touches the network when the
 * access token has actually expired and needs refreshing. Concurrent callers
 * share one resolution. A transient failure is not cached, so the next call
 * retries.
 */
export async function getCurrentUserId(): Promise<string | null> {
  if (cachedUserId !== undefined) return cachedUserId;

  if (!inflight) {
    inflight = (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        cachedUserId = data.session?.user?.id ?? null;
        return cachedUserId;
      } catch {
        // Leave cachedUserId unresolved so this retries rather than sticking
        // the whole app in a signed-out state over one bad read.
        return null;
      } finally {
        inflight = null;
      }
    })();
  }

  return inflight;
}
